import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, Mail, Copy } from "lucide-react";
import api from "@/lib/api";

// SalaryBox Import Result — shows newly-created accounts + send welcome emails
export default function ImportResultModal({ result, onClose, onSent }) {
  const created = result.accounts_created || [];
  const reset = result.accounts_reset || [];
  const defaultPw = result.default_password || "LD@2026!";
  const all = [...created, ...reset];

  const [senders, setSenders] = useState([]);
  const [senderId, setSenderId] = useState("");
  const [preferPersonal, setPreferPersonal] = useState(false);
  const [selected, setSelected] = useState(() => new Set(all.map((r) => r.user_id)));
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/invoice-senders").then((res) => {
      if (cancelled) return;
      const list = res.data || [];
      setSenders(list);
      if (list.length > 0) setSenderId(list[0].id);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const toggle = (uid) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    return next;
  });

  const copyRoster = async () => {
    const lines = ["email\tpassword"];
    all.forEach((r) => lines.push(`${r.email}\t${r.temp_password}`));
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      toast.success("Copied credentials roster to clipboard");
    } catch {
      toast.error("Clipboard blocked — please copy manually");
    }
  };

  const sendEmails = async () => {
    if (!senderId) { toast.error("Configure an SMTP sender in Invoices → Senders first"); return; }
    const recipients = all.filter((r) => selected.has(r.user_id)).map((r) => ({ user_id: r.user_id, temp_password: r.temp_password }));
    if (recipients.length === 0) { toast.error("Select at least one recipient"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/employees/send-welcome-emails", {
        sender_id: senderId,
        recipients,
        prefer_personal_email: preferPersonal,
        login_url: window.location.origin,
      });
      toast.success(`Welcome emails: ${data.sent} sent · ${(data.errors || []).length} errors`);
      onSent();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send welcome emails");
    }
    setSending(false);
  };

  return createPortal((
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose} data-testid="import-result-modal">
      <div className="brut-card max-w-3xl w-full flex flex-col min-h-0" style={{ maxHeight: "calc(100vh - 32px)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-900 bg-white shrink-0">
          <div>
            <h2 className="font-heading text-xl font-black">Accounts Provisioned</h2>
            <p className="font-mono text-[11px] text-zinc-500">{created.length} new logins · {reset.length} first-time password set · default: <b>{defaultPw}</b></p>
          </div>
          <button onClick={onClose} className="brut-btn-secondary p-1.5" data-testid="import-result-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="px-5 py-3 border-b border-zinc-200 bg-amber-50 flex flex-wrap gap-2 items-center text-xs font-mono shrink-0">
          <Mail className="w-3.5 h-3.5" />
          <span>Send welcome email via:</span>
          <select value={senderId} onChange={(e) => setSenderId(e.target.value)} className="brut-input px-2 py-1 text-xs font-mono" data-testid="welcome-sender-select">
            <option value="">— no sender configured —</option>
            {senders.map((s) => <option key={s.id} value={s.id}>{s.display_name || s.email}</option>)}
          </select>
          <label className="flex items-center gap-1 ml-2">
            <input type="checkbox" checked={preferPersonal} onChange={(e) => setPreferPersonal(e.target.checked)} data-testid="welcome-prefer-personal" />
            Prefer personal email
          </label>
          <button onClick={copyRoster} className="brut-btn-secondary px-2 py-1 text-[10px] font-mono flex items-center gap-1 ml-auto" data-testid="copy-roster-btn">
            <Copy className="w-3 h-3" /> COPY ROSTER
          </button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0">
          <table className="w-full text-xs">
            <thead className="bg-zinc-100 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 font-mono">
                  <input type="checkbox" checked={selected.size === all.length && all.length > 0} onChange={(e) => setSelected(e.target.checked ? new Set(all.map((r) => r.user_id)) : new Set())} data-testid="welcome-select-all" />
                </th>
                <th className="text-left px-3 py-2 font-mono">Name</th>
                <th className="text-left px-3 py-2 font-mono">Email</th>
                <th className="text-left px-3 py-2 font-mono">Password</th>
                <th className="text-left px-3 py-2 font-mono">Source</th>
              </tr>
            </thead>
            <tbody>
              {all.map((r) => (
                <tr key={r.user_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="px-3 py-1.5">
                    <input type="checkbox" checked={selected.has(r.user_id)} onChange={() => toggle(r.user_id)} data-testid={`welcome-select-${r.user_id}`} />
                  </td>
                  <td className="px-3 py-1.5 font-bold">{r.name}</td>
                  <td className="px-3 py-1.5 font-mono">
                    {r.email}
                    {r.email_auto_generated && <span className="ml-1 text-[9px] px-1 py-0.5 bg-yellow-200 border border-zinc-900">AUTO</span>}
                  </td>
                  <td className="px-3 py-1.5 font-mono">{r.temp_password}</td>
                  <td className="px-3 py-1.5 font-mono text-zinc-500">{created.includes(r) ? "new user" : "existing (first pw)"}</td>
                </tr>
              ))}
              {all.length === 0 && (
                <tr><td colSpan={5} className="p-10 text-center text-zinc-400 font-mono text-xs">No accounts provisioned in this import.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-between items-center gap-2 px-5 py-3 border-t border-zinc-900 bg-white shrink-0">
          <p className="font-mono text-[10px] text-zinc-500">Employees must change this password on first login.</p>
          <div className="flex gap-2">
            <button onClick={onClose} className="brut-btn-secondary px-4 py-2 text-sm">SKIP FOR NOW</button>
            <button onClick={sendEmails} disabled={sending || !senderId || selected.size === 0} className="brut-btn px-4 py-2 text-sm disabled:opacity-50 flex items-center gap-1" data-testid="send-welcome-emails-btn">
              <Mail className="w-4 h-4" /> {sending ? "SENDING..." : `SEND ${selected.size} EMAIL${selected.size === 1 ? "" : "S"}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  ), document.body);
}
